import {
  useCallback,
  useEffect, useState,
} from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import ImageSelectionList from './ImageSelectionList';
import InputImage from '../../Form/InputImage';
import { ImageItem } from '../../../contexts/ImagesContext';
import { ImageTypeValue, ImageType } from '../../../utils/image';
import styles from './ImageSelectionModal.module.css';
import ModalComponent from '../../ModalComponent';
import config from '../../../config';
import ImageModalPaginator from './ImageModalPaginator';
import { PaginationMetaLink } from '../../../models/Paginator';
import api from '../../../services/api';

interface Props {
  open: boolean
  handleClose: () => void
  handleSave: (item: ImageItem) => void
}

const ImageSelectionModal = (props: Props) => {
  const { open, handleClose, handleSave } = props;
  const [systemImages, setSystemImages] = useState<ImageItem[]>([]);
  const [myImages, setMyImages] = useState<ImageItem[]>([]);
  const [displayImages, setDisplayImages] = useState<ImageItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
  const [currentTab, setCurrentTab] = useState<ImageType | null>(null);
  const [imageMaxSizeError, setImageMaxSizeError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [systemImagePage, setSystemImagePage] = useState(1);
  const [myImagePage, setMyImagePage] = useState(1);
  const [systemImageLinks, setSystemImageLinks] = useState<PaginationMetaLink[]>([]);
  const [myImageLinks, setMyImageLinks] = useState<PaginationMetaLink[]>([]);
  const [systemImageTotal, setSystemImageTotal] = useState(0);
  const [myImageTotal, setMyImageTotal] = useState(0);
  const [systemImageCurrentPage, setSystemImageCurrentPage] = useState(1);
  const [myImageCurrentPage, setMyImageCurrentPage] = useState(1);
  const [systemImageFromItem, setSystemImageFromItem] = useState(0);
  const [systemImageToItem, setSystemImageToItem] = useState(0);
  const [myImageFromItem, setMyImageFromItem] = useState(0);
  const [myImageToItem, setMyImageToItem] = useState(0);

  const customNavTabs = `nav navTabs ${styles.customNavTabs}`;
  const customTabContent = `tab-content tabContent ${styles.customTabContent}`;

  const fetchImagesForTab = useCallback(async (type: ImageType, page?: number) => {
    setIsLoading(true);
    try {
      let targetPage = page;
      if (targetPage === undefined) {
        targetPage = type === ImageTypeValue.SYSTEM_IMAGE ? systemImagePage : myImagePage;
      }

      // Make API call directly to get pagination data
      const params = { type, page: targetPage };
      const res = await api.get('/v1/images', { params });

      if (res.data == null) {
        return;
      }

      const result = res.data.data.map((d: { id: number, url: string, thumbnail: string }) => ({
        id: d.id,
        url: d.url,
        thumbnail: d.thumbnail,
      }));

      // Capture pagination data from API response
      const paginationLinks = res.data.links || [];
      const paginationTotal = res.data.total || 0;
      const paginationCurrentPage = res.data.current_page || 1;
      const paginationFromItem = res.data.from || 0;
      const paginationToItem = res.data.to || 0;

      switch (type) {
        case ImageTypeValue.SYSTEM_IMAGE:
          setSystemImages(result);
          setDisplayImages(result);
          setSystemImageLinks(paginationLinks);
          setSystemImageTotal(paginationTotal);
          setSystemImageCurrentPage(paginationCurrentPage);
          setSystemImageFromItem(paginationFromItem);
          setSystemImageToItem(paginationToItem);
          if (targetPage !== undefined) {
            setSystemImagePage(targetPage);
          }
          break;
        case ImageTypeValue.MY_IMAGE:
          setMyImages(result);
          setDisplayImages(result);
          setMyImageLinks(paginationLinks);
          setMyImageTotal(paginationTotal);
          setMyImageCurrentPage(paginationCurrentPage);
          setMyImageFromItem(paginationFromItem);
          setMyImageToItem(paginationToItem);
          if (targetPage !== undefined) {
            setMyImagePage(targetPage);
          }
          break;
        default:
      }
    } finally {
      setIsLoading(false);
    }
  }, [systemImagePage, myImagePage]);

  useEffect(() => {
    // モーダル閉じる時に、タブをリセットします。
    if (!open) {
      setCurrentTab(null);
      setSystemImagePage(1);
      setMyImagePage(1);
      return;
    }
    setSelectedImage(null);
    setSystemImagePage(1);
    setMyImagePage(1);
    // モーダルがオープンした時に
    setCurrentTab(ImageTypeValue.SYSTEM_IMAGE);
  }, [open]);

  useEffect(() => {
    if (!open || currentTab === null) {
      return;
    }
    // Only fetch when tab changes, use current page state for that tab
    const page = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImagePage : myImagePage;
    fetchImagesForTab(currentTab, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentTab]);

  // サイドバーが切り替えた時に
  const handleTabChange = useCallback((type: ImageType) => {
    // Reset to page 1 when switching tabs
    if (type === ImageTypeValue.SYSTEM_IMAGE) {
      setSystemImagePage(1);
    } else if (type === ImageTypeValue.MY_IMAGE) {
      setMyImagePage(1);
    }
    setCurrentTab(type);
    // useEffect will handle fetching images when currentTab changes
  }, []);

  const handleImageUpload = async () => {
    setImageMaxSizeError(null);
    if (!currentTab) return;
    const page = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImagePage : myImagePage;
    fetchImagesForTab(currentTab, page);
  };

  const handlePageChange = useCallback((page: number) => {
    if (!currentTab) return;
    if (currentTab === ImageTypeValue.SYSTEM_IMAGE) {
      setSystemImagePage(page);
    } else if (currentTab === ImageTypeValue.MY_IMAGE) {
      setMyImagePage(page);
    }
    fetchImagesForTab(currentTab, page);
  }, [currentTab, fetchImagesForTab]);

  const currentLinks = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImageLinks : myImageLinks;
  const currentTotal = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImageTotal : myImageTotal;
  const currentPageNum = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImageCurrentPage : myImageCurrentPage;
  const currentFromItem = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImageFromItem : myImageFromItem;
  const currentToItem = currentTab === ImageTypeValue.SYSTEM_IMAGE ? systemImageToItem : myImageToItem;

  const paginatorSlot = currentLinks && currentLinks.length > 0 && currentPageNum && currentTotal > 0 ? (
    <ImageModalPaginator
      links={currentLinks}
      currentPage={currentPageNum}
      onPageChange={handlePageChange}
      total={currentTotal}
      fromItem={currentFromItem}
      toItem={currentToItem}
    />
  ) : null;

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <ModalComponent
        size="xl"
        title="Select Image"
        confirmButtonText="Select"
        onConfirm={() => {
          handleSave(selectedImage);
          setSelectedImage(null);
          setImageMaxSizeError(null);
        }}
        show={open}
        disabled={selectedImage === null}
        closeModal={() => {
          setImageMaxSizeError(null);
          handleClose();
        }}
        centered
        animation={false}
        bodyClassName="m-0 p-0"
        backdropClassName="z-index-4000"
        style={{ zIndex: 4001 }}
        aboveButtonsSlot={paginatorSlot}
      >
        <div className="tab modalTabs">
          <ul className={customNavTabs} role="tablist" style={{ overflow: 'auto' }}>
            <li>
              <a
                onClick={() => {
                  if (!isLoading) {
                    handleTabChange(ImageTypeValue.SYSTEM_IMAGE);
                  }
                }}
                className={currentTab === ImageTypeValue.SYSTEM_IMAGE ? 'active' : ''}
                href="#tabImage_1"
                data-bs-toggle="tab"
                role="tab"
                style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}
              >
                Stock Images
              </a>
            </li>
            <li>
              <a
                onClick={() => {
                  if (!isLoading) {
                    handleTabChange(ImageTypeValue.MY_IMAGE);
                  }
                }}
                className={currentTab === ImageTypeValue.MY_IMAGE ? 'active' : ''}
                href="#tabImage_1"
                data-bs-toggle="tab"
                role="tab"
                style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}
              >
                My Images
              </a>
            </li>
          </ul>
          <div className={customTabContent}>
            <div id="tabImage_1" className="tab-pane tabPane active">
              <div className="imageUpload">
                <Box sx={{ display: currentTab === ImageTypeValue.MY_IMAGE ? 'block' : 'none' }}>
                  <Button
                    className="btn btn-primary"
                    variant="contained"
                    component="label"
                    disabled={isLoading}
                    sx={{
                      position: 'relative',
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={15} />
                    ) : (
                      <>
                        <InputImage
                          type={currentTab}
                          id="id"
                          isDisabled={false}
                          fileHandler={handleImageUpload}
                          setIsLoading={setIsLoading}
                          onMaxFileSizeExceeded={(message) => {
                            setImageMaxSizeError(message);
                          }}
                        />
                        {/* PDEV3-34【3】: wrap bare text so auto-translate's <font> stays inside a stable element */}
                        <span>Upload image ...</span>
                      </>
                    )}
                  </Button>
                  <span style={{ marginLeft: 5 }}>{`*Max image file size is ${config.image.maxFileSizeInMb}MB.`}</span>
                  {imageMaxSizeError && (
                    <div className="text-danger mt-2">
                      {imageMaxSizeError}
                    </div>
                  )}
                </Box>
              </div>
              <ImageSelectionList
                images={displayImages}
                isLoading={isLoading}
                handleSelected={(id, url, thumbnail) => {
                  setSelectedImage({
                    id,
                    url,
                    thumbnail,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default ImageSelectionModal;
